import { AnyAction, createSlice } from '@reduxjs/toolkit';
import createGenericSlice from '@/store/reducers/sliceTemplate';
import { ResultState } from '@/store/types/resultTypes';
import { apiUrl } from '@/api/axios';
import { getTranslation } from '@/core/utils/i18nHelper';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { answersSent } from '@/store/reducers/testingSlice';
import { setCurrentRating } from '@/store/reducers/testRatingSlice';
import { setRatingFormEmail } from '@/store/reducers/testRatingFormSlice';
import API_ENDPOINTS from '@/api/apiConfig';
import { setPageData } from '@/store/reducers/pageSlice';

const initialResultData = {
	attempt: {
		attempt_key: '', // уникальный ключ попытки
		attempt_url: '', // для блока копировать результат
		results: [], // тексты стенайнов
		show_soft_404: false, // тест был модифицирован и результаты неактуальны
		template: '', // todo в будущем attempt.template
		avg_attempt_time: 0, // среднее время прохождения теста всеми пользователями
		duration_attempt_time: 0, // мое время прохождения теста
	},

	breadcrumbs: [],

	device: {
		country: '', // страна пользователя
		app: false, // приложение
	},

	test: {
		id: null,
		name: '', // название теста
		url: '', // для роутинга в будущем
		result_header: '', // результат header
		result_footer: '', // результат footer
		theme: '', // тема теста
	},

	// todo adv подумать Саше
	adv: {
		show_adv: true,
	},

	blocks: {
		resultBreadcrumb: { show: false, content: '' }, // хлебные крошки в header
		resultBtnListTests: { show: false, content: '' }, // в список тестов
		resultHistory: { show: false, content: '' }, // история результатов
		resultHeader: { show: false, content: '' }, // описание результата
		resultHeaderPersonal: { show: false, content: '' }, // шапка описания результата
		resultFooter: { show: false, content: '' }, // описание результата в футере
		resultFooterPersonal: { show: false, content: '' }, // todo ??  футер описания результата
		replaceResult: { show: false, content: '' }, // todo ?? не выводим результат и заменяем из контента -- что именно заменяем - только описание
		socialLinks: { show: false, content: '' }, // соц сети ТМ
		appLinks: { show: false, content: '' }, // приложения ТМ
		resultRating: { show: false, content: '' }, // установка рейтинга и общий рейтинг теста
		recommendTests: { show: false, content: '' }, // рекомендованные тесты
		advFlocktory: { show: false, content: '' }, // реклама flocktory
		advResultTop: { show: false, content: '' }, // реклама
		advResultBottom: { show: false, content: '' }, // пока его нет
		resultRecommendForYou: { show: false, content: '' }, // рекомендации курсов и пр.
		resultRecommendForYouWidget: { show: false, content: '' }, // todo ?? виджет рекомендаций
		resultRecommendBooks: { show: false, content: '' }, // рекомендации книг
		resultRecommendText: { show: false, content: '' }, // рекомендации текст
		forResultNeedSpecialist: { show: false, content: '' }, // не воспринимать результаты всерьез
	},
	user: {
		auth: false,
		email: '' // для авто вставки в форму рейтинга
	},
	recommend_for_you: { // рекомендуем лично вам
		recommends: [],
		litres: [],
		texts: []
	},
	incorrect_answers: [], // неправильные ответы с пояснением
}


const additionalState: Partial<ResultState> = {
    resultData: initialResultData,
	isResultShown: false
};

const resultSlice = createGenericSlice<ResultState>('result', additionalState);

const specificReducers = {
    setResult(state: ResultState, action: AnyAction) {
        state.resultData = action.payload;
    },

    resetResult(state: ResultState, action: AnyAction) {
        state.resultData = initialResultData;
    },
};

const combinedSlice = createSlice({
    name: 'result',
    initialState: resultSlice.getInitialState() as ResultState,
    reducers: {
        ...resultSlice.caseReducers,
        ...specificReducers,
    },
});

export const {
    fetchDataStart,
	fetchDataSuccess,
    fetchDataError,
    setResult,
    resetResult,
} = combinedSlice.actions;

export default combinedSlice.reducer;
export { combinedSlice as resultSlice };


const processResultResponse = (resultResponse: any, dispatch: any) => {
	dispatch(setResult(resultResponse));

	if (resultResponse.rating) {
		dispatch(
			setCurrentRating({
				rating: resultResponse.rating.rating,
				ratingPercent: resultResponse.rating.ratingPercent,
				isOwnAttempt: resultResponse.rating.isOwnAttempt,
				isUsrVoted: resultResponse.rating.isUsrVoted,
			})
		);
	}

	if (resultResponse.page) {
		dispatch(setPageData(resultResponse.page));
	}

	if (resultResponse.user && resultResponse.user.email) {
		dispatch(setRatingFormEmail(resultResponse.user.email));
	}

	dispatch(fetchDataSuccess());
};

export const postResults = (answers: any) => async (dispatch, getState) => {

	const testId = getState().testing.testId;
	const durations = getState().duration.durations;

	try {
		const response = await apiUrl.post(
			API_ENDPOINTS.results,
			{
				test_id: testId,
				durations: durations,
				answers: answers,
			},
			{
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
			}
		);

		const resultResponse = response.data.data;

		dispatch(answersSent(getTranslation('testing.success')));
		processResultResponse(resultResponse, dispatch);

	} catch (error) {
		console.error('Error while posting results:', error);
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};


export const fetchResult = (id: number) => async (dispatch, getState) => {
	try {
		const existingResult = getState().result.resultData;

		if (existingResult && Object.keys(existingResult).length > 0 && existingResult.attempt.attempt_key) {
			return; // Если данные уже загружены, повторный запрос не выполняется
		}

		dispatch(fetchDataStart());

		const response = await apiUrl.get(`${API_ENDPOINTS.lastAttempt}${id}`);
		const resultResponse = response.data.data;
		processResultResponse(resultResponse, dispatch);
	} catch (error) {

		console.error('Error while fetching results:', error);
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};

export const fetchResultByAttemptKey = (attemptKey: string) => async (dispatch: any) => {
	try {
		dispatch(fetchDataStart());
		const response = await apiUrl.get(`/api/attempts/${attemptKey}`);
		const resultResponse = response.data.data;
		processResultResponse(resultResponse, dispatch);
	} catch (error) {
		console.error('Error while get result by attemptKey:', error);
		handleResponseError(error, (data) => dispatch(fetchDataError(data)));
	}
};